import React from 'react';
import { ResponsiveLine, Point } from '@nivo/line';

import { SeverityLayer, BottomLineLayer, CustomSymbol } from './CustomLayers';

import { formatChartData } from '../../../utils/assessmentUtils';
import { populateArray } from '../../../utils/arrayUtils';
import { LineData, DataPoint, PHQData } from '../../../types';
import Box from '../../Box';

const TICKS_TO_STRINGS = new Map([
  [22.5, '20 - 24'],
  [21.5, 'severe'],
  [18, '15 - 19'],
  [17, 'moderate'],
  [16, 'severe'],
  [12.5, '10 - 14'],
  [11.5, 'moderate'],
  [7.5, '5 - 9'],
  [6.5, 'mild'],
  [2, '0 - 4'],
  [1, 'none'],
  [-3.5, 'Incomplete'],
  [-4.5, 'assessments'],
]);

// makeFillerData returns a list of data points each a week apart for every datapoint missing.
const makeFillerData = (lineData: LineData, numberOfWeeks: number) => {
  const { data } = lineData;
  const expectedDataLength = data.length < numberOfWeeks ? numberOfWeeks : data.length + 1;
  const recentPHQDate = data.length > 0 ? new Date(data[data.length - 1].x) : new Date();
  recentPHQDate.setDate(recentPHQDate.getDate() + 7);
  return populateArray(expectedDataLength - data.length, recentPHQDate.getTime()).map((value, index): DataPoint => {
    const newDate = new Date(value);
    newDate.setDate(newDate.getDate() + (7 * (index)));
    return {
      x: newDate,
      y: null,
    };
  });
};

// getDateWithAdjustment returns a copy of the date adjusted by the number of days provided.
const getDateWithAdjustment = (date: Date, adjustByDays: number) => {
  const dateCopy = new Date(date);
  dateCopy.setDate(dateCopy.getDate() + adjustByDays);
  return dateCopy;
};

type Props = {
  chartId: string;
  phqs: Array<PHQData>;
  selectedPHQ: PHQData;
  onSelectPHQ: (phq: PHQData) => void;
  numberOfWeeks: number;
  height: number;
}
const PHQChart: React.FC<Props> = (props: Props) => {
  const {
    chartId,
    phqs,
    numberOfWeeks,
    selectedPHQ,
    onSelectPHQ,
    height,
  } = props;

  const {
    phqData,
    dateScoreMap,
  } = formatChartData(chartId, phqs);

  // Get filler data for the number of weeks we expect to show in the chart.
  const withFillerData = [...phqData.data, ...makeFillerData(phqData, numberOfWeeks)];
  const firstDate = withFillerData[0].x;
  const lastDate = withFillerData[withFillerData.length - 1].x;
  // Pad data with null values for 2 days before and after.
  const adjustedData = [
    {
      x: getDateWithAdjustment(firstDate, -2),
      y: null,
    },
    ...withFillerData,
    {
      x: getDateWithAdjustment(lastDate, 2),
      y: null,
    },
  ];

  // Get axis ticks excluding the padding.
  const xAxisTicks = adjustedData.slice(1, -1).map((dataPoint) => dataPoint.x);

  const lineData = [{
    data: adjustedData,
    id: phqData.id,
  }];

  return (
    <Box position="relative">
      <Box position="relative" height="100%" width="100%">
        <Box height={`${height}px`} minWidth="400px">
          <ResponsiveLine
            data={lineData}
            lineWidth={2}
            colors={['#FFA584']}
            margin={{
              top: 35,
              right: 30,
              bottom: 35,
              left: 80,
            }}
            xScale={{
              type: 'time',
              format: 'native',
              useUTC: false,
              precision: 'day',
            }}
            xFormat="time:%Y-%m-%d"
            yScale={{
              type: 'linear',
              min: -7,
              max: 25,
              stacked: false,
              reverse: false,
            }}
            yFormat=" >-.2f"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              format: '%m/%d/%Y',
              tickValues: xAxisTicks,
              legendOffset: -12,
              tickRotation: -20,
            }}
            axisLeft={{
              tickValues: Array.from(TICKS_TO_STRINGS.keys()),
              tickPadding: 10,
              format: (value) => `${TICKS_TO_STRINGS.get(value as number)}`,
            }}
            pointSize={12}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            pointSymbol={CustomSymbol(selectedPHQ, dateScoreMap)}
            enableSlices={false}
            useMesh
            onMouseEnter={(_: Point, event: React.MouseEvent) => {
              const eventCopy = ({ ...event } as React.MouseEvent<HTMLCanvasElement>);
              eventCopy.currentTarget.style.cursor = 'pointer';
            }}
            layers={[
              'markers',
              'axes',
              'areas',
              'slices',
              BottomLineLayer(lineData[0].data),
              'mesh',
              SeverityLayer,
              'points',
            ]}
            onClick={(point) => point && onSelectPHQ(phqs.find((phq) => (
              (new Date(phq.completeTime)).getTime() === (new Date(point.data.x)).getTime()
            )))}
            tooltip={(point) => {
              const yValue = point?.point?.data?.yFormatted;
              const scoreDisplay = parseInt(`${yValue}`, 10) >= 0
                ? `assessment score: ${yValue}`
                : 'incomplete assessment';
              return (
                <Box
                  bgcolor="#FEFEFE"
                  borderRadius="2px"
                  padding="8px 16px"
                  transform="translate(0px, 85px)"
                  boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.18)"
                >
                  <Box>
                    {`assessment date: ${point.point.data.xFormatted}`}
                  </Box>
                  <Box>
                    {scoreDisplay}
                  </Box>
                </Box>
              );
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PHQChart;
